import * as React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import tw from 'twin.macro';
import { useIntercom } from 'react-use-intercom';
import { UseFormReturn } from 'react-hook-form';

import { Button } from '@restworld/ui-ds-base';
import { CreateEmployerRestaurantPayload } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';

import { activeAddressIdAtom, googleAddresInputValueAtom } from './google-address-input-result';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import useRestaurantOnboardingMutationQuery from './restaurant-onboarding-mutation-query';
import { activeRestaurantOnboardingStateAtom } from '.';
import {
  useAccountAlreadyExistOrNotMutation,
  useFormatRestaurantOnboardingData,
  useGettingStartedPage,
} from './restaurant-onboarding-modal.hooks';

export const useRestaurantCreateSubmit = ({
  methods,
}: {
  methods: UseFormReturn<RestaurantOnboardingPayload, any>;
}) => {
  const formatFormData = useFormatRestaurantOnboardingData();

  const { mutate: onOnboardingRestaurant, isPending: isRestaurantOnboarding } = useRestaurantOnboardingMutationQuery();

  const onRestaurantOnboardingFormSubmit = methods.handleSubmit(
    React.useCallback(
      (data) => {
        const formattedData = formatFormData(data);
        // onboarding new restaurant
        onOnboardingRestaurant(formattedData as CreateEmployerRestaurantPayload);
      },
      [formatFormData, onOnboardingRestaurant]
    )
  );
  return {
    onRestaurantOnboardingFormSubmit,
    isRestaurantOnboarding,
  };
};
export const RestaurantOnboardingFooter: React.FC<{
  methods: UseFormReturn<RestaurantOnboardingPayload, any>;
}> = ({ methods }) => {
  const { t: tC } = useTranslation('common');
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const activeItem = useAtomValue(activeAddressIdAtom);
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);
  const isNoneOfResultsSelected = activeItem === 'none_of_the_results';
  const isInputFilled = googleInputAddressValue.length > 0;
  const { showMessages: showIntercomMessage } = useIntercom();
  const { onRestaurantOnboardingFormSubmit, isRestaurantOnboarding } = useRestaurantCreateSubmit({ methods });
  const isGettingStartedPage = useGettingStartedPage();

  const { mutate: checkUserExistence, isPending } = useAccountAlreadyExistOrNotMutation();
  const { trackPHEvent } = useAnalytics();

  React.useEffect(() => {
    Object.keys(methods.formState.errors).forEach((key) => {
      const element = document.getElementById(key);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [methods.formState]);

  const handleContinue = React.useCallback(() => {
    if (isNoneOfResultsSelected) {
      showIntercomMessage();
      return;
    }
    if (activeUI === 'select_restaurant_from_google') {
      setActiveUI('magic_ai_loading');
      return;
    }
    if (isGettingStartedPage) {
      if (activeUI === 'restaurant_onboarding_form') {
        methods.trigger().then((v) => {
          if (v) {
            setActiveUI('restaurant_owner_onboarding_form');
            trackPHEvent('freemium_employer_confirms_restaurant_data', {});
          }
        });
      } else if (activeUI === 'restaurant_owner_onboarding_form') {
        methods.trigger().then((v) => {
          if (v)
            checkUserExistence({
              email: methods.getValues('email'),
              phone_number: methods.getValues('phone_number'),
            });
        });
      }
      return;
    }
    if (activeUI === 'restaurant_onboarding_form') {
      onRestaurantOnboardingFormSubmit();
      return;
    }
  }, [
    activeUI,
    checkUserExistence,
    isGettingStartedPage,
    isNoneOfResultsSelected,
    methods,
    onRestaurantOnboardingFormSubmit,
    setActiveUI,
    showIntercomMessage,
    trackPHEvent,
  ]);

  const shouldHideButton = React.useMemo(
    () =>
      activeUI === 'restaurant_onboarding_successfull' ||
      (activeUI === 'select_restaurant_from_google' && !isInputFilled) ||
      activeUI === 'magic_ai_loading',
    [activeUI, isInputFilled]
  );

  if (shouldHideButton) return null;

  return (
    <div tw="flex justify-center items-center w-full md:(max-w-[38rem] min-w-[38rem] mx-auto relative -left-2)">
      <Button
        loading={isRestaurantOnboarding || isPending}
        onClick={handleContinue}
        disabled={!activeItem}
        size="md"
        twStyle={tw`w-full`}
        variant="primary"
      >
        {isNoneOfResultsSelected ? tC('contact_support') : tC('continue')}
      </Button>
    </div>
  );
};
